import React, {
    ReactElement, useEffect, useState,
} from "react";
import { Form } from "react-bootstrap";
import {
    ChatSettings,
    ChatSettingsChannelEmail,
    ChatSettingsChannelMenu, ChatSettingsDeflection, ChatSettingsIdentitiesType,
    ChatSettingsInput, ChatSettingsMicrosoftTeams, ChatSettingsSlack, GetChatSettingsDocument, GetSlackChannelSettingsDocument,
    IntegrationActionType, Settings,
    useGetChatSettingsQuery, useSaveChatSettingsMutation,
} from "../../../../__generated__/api";
import { isDeepEqual, removeTypename } from "../../../../api";
import { Loading } from "../../../../components";
import useDirtyTracker from "../../../../components/dirty-tracker/DirtyTracker";
import Editor, { DEFAULT_TEXT } from "../../../../components/editor/Editor";
import ExpandingSection from "../../../../components/expanding-section/ExpandingSection";
import Integrations from "../../../../components/pickers/integrations/Integrations";
import RequestTypes from "../../../../components/pickers/request-types/RequestTypes";
import Select from "../../../../components/select/Select";
import useRouting, { useParams } from "../../../../contexts/Routing";
import { ErrorRemote } from "../../../error/Error";
import { SettingsProps } from "../base";
import WelcomeMessageHelp from "../channels/slack/WelcomeMessageHelp";
import ChannelMenuSettings from "./settings/ChannelMenu";
import DeflectionSettings from "./settings/Deflection";
import EmailSettings from "./settings/Email";
import MicrosoftTeamsSettings from "./settings/MicrosoftTeams";
import SlackSettings from "./settings/Slack";

export function DisplayChatSettings({
    chatSettings, settings, onChange, shouldAllowSlack, shouldAllowMicrosoftTeams, shouldAllowAutomation, shouldAllowEmail,
}: {
    chatSettings: ChatSettings | undefined;
    settings: Settings | undefined;
    onChange: (input: ChatSettingsInput) => void;
    shouldAllowSlack?: boolean;
    shouldAllowMicrosoftTeams?: boolean;
    shouldAllowAutomation?: boolean;
    shouldAllowEmail?: boolean;
}): ReactElement {
    const [welcomeMessage, setWelcomeMessaage] = useState(() => chatSettings?.welcomeMessage?.rawText ?? "");
    const [identities, setIdentities] = useState<string[]>(() => chatSettings?.identities ?? []);
    const [identitiesType, setIdentitiesType] = useState<ChatSettingsIdentitiesType>(
        () => chatSettings?.identitiesType ?? ChatSettingsIdentitiesType.Inherit,
    );

    const [formIds, setFormIds] = useState<string[]>(() => chatSettings?.formIds ?? []);
    const [slaTierId, setSlaTierId] = useState<string>(() => chatSettings?.slaTierId ?? undefined);

    const [channelMenu, setChannelMenu] = useState(() => chatSettings?.options?.channelMenu);

    const [slack, setSlack] = useState(() => chatSettings?.options?.slack);
    const [microsoftTeams, setMicrosoftTeams] = useState(() => chatSettings?.options?.microsoftTeams);

    const [email, setEmail] = useState(() => chatSettings?.options?.email);

    const [deflection, setDeflection] = useState(() => chatSettings?.options?.deflection);

    const [state, setState] = useState<ChatSettingsInput>();
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        if (!chatSettings || chatSettings === state) {
            return;
        }
        setWelcomeMessaage(chatSettings.welcomeMessage?.rawText ?? "");
        setIdentities(chatSettings.identities ?? []);
        setIdentitiesType(chatSettings.identitiesType ?? ChatSettingsIdentitiesType.Inherit);
        setFormIds(chatSettings.formIds ?? []);
        setChannelMenu(chatSettings.options?.channelMenu);
        setSlack(chatSettings.options?.slack);
        setMicrosoftTeams(chatSettings.options?.microsoftTeams);
        setEmail(chatSettings.options?.email);
        setDeflection(chatSettings.options?.deflection);
        setSlaTierId(chatSettings.slaTierId);
        setIsLoaded(true);
    }, [chatSettings]);

    type TypeName = Parameters<typeof removeTypename>[0];

    useEffect(() => {
        if (!isLoaded) {
            return;
        }
        const value: ChatSettingsInput = removeTypename<ChatSettingsInput & TypeName>({
            welcomeMessage: welcomeMessage ? { rawText: welcomeMessage } : undefined,
            identities,
            identitiesType,
            formIds,
            slaTierId,
            options: {
                channelMenu,
                slack: {
                    ...slack,
                    canHaveEmojiActions: undefined,
                },
                deflection,
                email,
                microsoftTeams,
                tag: chatSettings?.options?.tag,
            },
        } as TypeName);

        if (!isDeepEqual(value, state)) {
            setState(value);
            onChange(value);
        }
    }, [welcomeMessage, identities, formIds, slaTierId, channelMenu, slack, email, microsoftTeams, deflection]);

    const currentSlaTier = slaTierId ? settings?.sla?.tiers?.find(tier => tier.id === slaTierId) : undefined;

    return (
        <div>
            <div className="border p-3 rounded bg-white">
                <h5 className="mb-3 text-light">
                    Chat Settings
                </h5>

                <Form.Group className="mb-3">
                    <Form.Label className="fw-bolder">Request Types</Form.Label>
                    <RequestTypes
                        isMulti
                        placeholder="Which service should be selected"
                        onSelected={selected => {
                            setFormIds(selected);
                        }}
                        value={formIds}
                    />
                    <Form.Text>
                        When a user asks a question, thse are the request types the user can fill out.
                        To order the request types, click and drag them inside this box.
                    </Form.Text>
                </Form.Group>

                <ExpandingSection title="Advanced Settings" classBackground="bg-gray-100">

                    <Form.Group className="mb-4">
                        <Form.Label className="fw-bolder">
                            Welcome Message
                            {" "}
                            <WelcomeMessageHelp />
                        </Form.Label>
                        <div className="bg-white border-light border rounded">
                            <Editor
                                text={welcomeMessage as string || DEFAULT_TEXT}
                                onChange={msg => {
                                    setWelcomeMessaage(msg);
                                }}
                                shouldFocus
                                placeholder="Hey, please remember to use a thread..."
                            />
                        </div>
                        <Form.Text>
                            When a customer first asks a question, this is the message they will
                            first see in this channel. If you want to overwrite with no message,
                            make the defaut message &quot;none&quot;.
                            {" "}
                            <WelcomeMessageHelp />
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bolder">Ticket Integration</Form.Label>
                        <Integrations
                            placeholder="Which integration should create a ticket"
                            onSelected={(selected, type) => {
                                setIdentities(selected ? [selected] : []);
                                setIdentitiesType(type);
                            }}
                            canSetNone
                            isInheritable
                            identitiesType={identitiesType}
                            value={identities.find(identity => identity.startsWith("urn:fq:integration"))}
                            actions={[IntegrationActionType.CreateTicket]}
                        />
                        <Form.Text>
                            When creating a new ticket, Agent can automatically create a ticket in a 3rd party
                            services such as Zendesk, Intercom, Atlassian, or something else. Which existing
                            integration would you like to use for this?

                        </Form.Text>
                    </Form.Group>

                    {settings?.sla?.tiers?.isNotEmpty && (
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bolder">SLA Tier</Form.Label>
                            <Select
                                options={settings?.sla?.tiers?.map(tier => ({
                                    label: tier.name as string,
                                    value: tier.id as string,
                                    data: tier,
                                })) ?? []}
                                isClearable
                                onChange={tier => {
                                    setSlaTierId(tier?.data?.id);
                                }}
                                value={!currentSlaTier ? null : {
                                    label: currentSlaTier.name as string,
                                    value: currentSlaTier.id as string,
                                    data: currentSlaTier,
                                }}
                            />
                            <Form.Text>
                                Which SLA tier should apply to this channel?
                            </Form.Text>
                        </Form.Group>
                    )}
                </ExpandingSection>

            </div>

            {(slack || shouldAllowSlack) && (
                <>
                    <SlackSettings
                        data={slack as ChatSettingsSlack}
                        shouldAllowAutomation={shouldAllowAutomation}
                        tag={chatSettings?.options?.tag}
                        onChange={value => {
                            setSlack(value);
                        }}
                    />

                    <ChannelMenuSettings
                        data={channelMenu as ChatSettingsChannelMenu | undefined}
                        onChange={value => {
                            setChannelMenu(value);
                        }}
                    />
                </>
            )}

            {(microsoftTeams || shouldAllowMicrosoftTeams) && (
                <>
                    <MicrosoftTeamsSettings
                        data={microsoftTeams as ChatSettingsMicrosoftTeams}
                        canAddTags={chatSettings?.options?.tag == null}
                        onChange={value => {
                            setMicrosoftTeams(value);
                        }}
                    />

                    <ChannelMenuSettings
                        data={channelMenu as ChatSettingsChannelMenu | undefined}
                        onChange={value => {
                            setChannelMenu(value);
                        }}
                    />
                </>
            )}

            {(email || shouldAllowEmail) && (
                <EmailSettings
                    data={email as ChatSettingsChannelEmail | undefined}
                    onChange={value => {
                        setEmail(value);
                    }}
                />
            )}

            <DeflectionSettings
                data={deflection as ChatSettingsDeflection}
                onChange={value => {
                    setDeflection(value);
                }}
            />

        </div>
    );
}
DisplayChatSettings.defaultProps = {
    shouldAllowSlack: false,
    shouldAllowAutomation: false,
    shouldAllowEmail: false,
    shouldAllowMicrosoftTeams: false,
};

export default function Chat(props: SettingsProps): ReactElement {
    const { Paths } = useRouting();
    const { id } = useParams(Paths.ADMINISTRATOR_SETTING_ID);
    const [save] = useSaveChatSettingsMutation();

    const { data, error, loading: isLoading } = useGetChatSettingsQuery({
        fetchPolicy: "no-cache",
        variables: {
            id,
        },
    });
    const [settings, setSettings] = useState(data?.chatSettings);

    const dirtyTracker = useDirtyTracker({
        finishedSavingMessage: "Channel Settings Saved",
        onSave: (event, onDone) => {
            event.preventDefault();
            if (!settings) {
                return;
            }
            (async () => {
                const options = {
                    ...settings?.options,
                };
                delete options.tag;

                await save({
                    refetchQueries: [
                        { query: GetSlackChannelSettingsDocument },
                        { query: GetChatSettingsDocument, variables: { id } },
                    ],
                    variables: {
                        id,
                        settings: {
                            ...settings,
                            options,
                        },
                    },
                });
                onDone();
            })();
        },
    });

    // eslint-disable-next-line arrow-body-style
    dirtyTracker.useWatchDeepCompareProperty(settings, data?.chatSettings, (path, left, right) => {
        if (path.lastElement === "welcomeMessage"
        || path.lastElement === "afterText") {
            return dirtyTracker.isEditorTextEqual(left, right);
        }
        if (path.lastElement === "feedbackIntroduction") {
            return dirtyTracker.isHtmlEqual(left as string, right as string);
        }
        if (Array.isArray(left) && left.isEmpty && right == null) {
            return true;
        }
        return undefined;
    });

    dirtyTracker.useLoad(() => {
        if (data) {
            setSettings(data?.chatSettings);
        }
    }, [data?.chatSettings]);

    if (isLoading) {
        return <Loading />;
    }
    if (error) {
        return <ErrorRemote error={error} />;
    }

    return (
        <form className="flex-grow-1">

            <DisplayChatSettings
                chatSettings={settings as ChatSettings}
                settings={props.settings}
                onChange={setSettings}
                shouldAllowAutomation={data?.chatSettings?.options?.slack?.canHaveEmojiActions !== false}
            />

            {dirtyTracker.saveComponent}
        </form>
    );
}
